'use client';

import type { IconColor } from '@volvo-cars/react-icons';
import { Icon } from '@volvo-cars/react-icons';
import { type AnchorHTMLAttributes, type FC, Fragment, useState } from 'react';

export type InlineLinkProps = Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'children' | 'href'
> & {
  href: string;
  children: string;
  forceNewTab?: true;
  trackEventAction?: string;
  trackEventLabel?: string;
};

const InlineLink: FC<InlineLinkProps> = ({
  href,
  children,
  forceNewTab,
  trackEventAction = 'link|click',
  trackEventLabel,
  className,
  rel,
  onMouseEnter,
  onMouseLeave,
  ...props
}) => {
  const [onHover, setHover] = useState(false);

  const isExternal = openNewTab(href);
  const newTab = forceNewTab || isExternal;

  const resolvedClassName = ['link-inline', 'text-secondary', className]
    .filter(Boolean)
    .join(' ');
  const resolvedRel = newTab ? (rel ?? 'noopener noreferrer') : rel;

  return (
    <a
      href={href}
      target={newTab ? '_blank' : undefined}
      rel={resolvedRel}
      className={resolvedClassName}
      data-track-event-action={trackEventAction}
      data-track-event-label={trackEventLabel ?? href}
      onMouseEnter={(event) => {
        setHover(true);
        onMouseEnter?.(event);
      }}
      onMouseLeave={(event) => {
        setHover(false);
        onMouseLeave?.(event);
      }}
      {...props}
    >
      {newTab ? (
        <NewTab onHover={onHover} isExternal={isExternal}>
          {children}
        </NewTab>
      ) : (
        children
      )}
    </a>
  );
};

const NewTab: FC<{
  onHover: boolean;
  isExternal: boolean;
  children: string;
}> = ({ onHover, isExternal, children }) => {
  const words = children.trim().split(/\s+/).filter(Boolean);

  if (words.length === 0) {
    return <>{children}</>;
  }

  const wordCount: Record<string, number> = {};

  return (
    <>
      {words.map((word) => {
        wordCount[word] = (wordCount[word] ?? 0) + 1;
        const key = `${word}-${wordCount[word]}`;
        const isLastWord =
          key === `${words[words.length - 1]}-${wordCount[word]}` &&
          word === words[words.length - 1];

        return (
          <Fragment key={key}>
            {isLastWord ? (
              <span className="inline-flex items-center">
                {word}
                <span className="inline-flex items-center ml-4">
                  <Icon
                    icon="external-link"
                    size={16}
                    color={onHover ? 'primary' : getColor(isExternal)}
                  />
                </span>
              </span>
            ) : (
              <span>{word} </span>
            )}
          </Fragment>
        );
      })}
    </>
  );
};

const openNewTab = (href: string) => /^https?:\/\//.test(href);

const getColor = (isExternalLink: boolean): IconColor =>
  isExternalLink ? 'accent-blue' : 'secondary';

export default InlineLink;
